import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';
import { PillButton } from '../AudienceIntro/_shared';

/* ------------------------------------------------------------------ *
 * "Darček k nákupu nad 40 €" banner for the /sady page (below the
 * Skvelko box). A standalone copy of the SelfIntro promo DarcekCard —
 * deliberately NOT shared with it, so the homepage card stays as is.
 * Mobile: identical to the SelfIntro card (red collage banner on top,
 * text + CTA below). Desktop (md+): stretched into a low full-width
 * strip (Figma image-daffer-40-40-banner) — collage panel left, the
 * "Darček k nákupu" pill pinned on it, title + description in the
 * middle, CTA pill on the right, all vertically centred.
 * ------------------------------------------------------------------ */

const ASSET = '/images/self-intro';

const MAROON = '#700e1e';
const RED = '#e3173e';
const DARK = '#231f20';

const CARD_SHADOW = `0 ${rem(6)} ${rem(60)} rgba(0, 0, 0, 0.06), 0 ${rem(
  4,
)} ${rem(8)} rgba(0, 0, 0, 0.04)`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(12)};
  box-shadow: ${CARD_SHADOW};
  margin: ${rem(16)} 0 0;
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    align-items: stretch;
    border-radius: ${rem(16)};
    /* The 5px sides mirror the FilterWrapper padding around the Skvelko box
       above, so the banner edges line up with it. */
    margin: ${rem(24)} ${rem(5)} 0;
  }
`;

const Img = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: ${rem(240)};
  background: #bd1933;
  ${({ theme }) => theme.media('md')} {
    width: auto;
    flex: 0 0 40%;
    height: auto;
    min-height: ${rem(134)};
  }
`;

const Collage = styled.img`
  position: absolute;
  left: 50%;
  width: ${rem(350)};
  bottom: -25%;
  max-width: none;
  transform: translateX(-50%) rotate(8deg);
  ${({ theme }) => theme.media('md')} {
    width: 56%;
    bottom: auto;
  }
`;

const Badge = styled.img`
  position: absolute;
  z-index: 1;
  left: 0;
  bottom: 0;
  max-width: 44%;
  max-height: 80%;
  pointer-events: none;
`;

const ImgTag = styled.span`
  display: none;
  ${({ theme }) => theme.media('md')} {
    position: absolute;
    z-index: 2;
    top: ${rem(14)};
    right: ${rem(20)};
    display: inline-flex;
    align-items: center;
    gap: ${rem(6)};
    background: ${({ theme }) => theme.color.white};
    border: ${rem(1)} solid ${RED};
    border-radius: ${rem(999)};
    padding: ${rem(6)} ${rem(12)};
    font-size: ${rem(14)};
    font-weight: 600;
    line-height: 1.7;
    color: #bd1933;

    img {
      width: ${rem(20)};
      height: ${rem(20)};
    }
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(24)} ${rem(20)};
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: row;
    align-items: center;
    gap: ${rem(32)};
    padding: ${rem(16)} ${rem(32)};
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  ${({ theme }) => theme.media('md')} {
    flex: 1 1 auto;
    min-width: 0;
    gap: ${rem(6)};
  }
`;

const GiftTag = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  border: ${rem(1)} solid ${RED};
  border-radius: ${rem(999)};
  padding: ${rem(6)} ${rem(12)};
  font-size: ${rem(14)};
  font-weight: 600;
  line-height: 1.7;
  color: #bd1933;

  img {
    width: ${rem(20)};
    height: ${rem(20)};
  }

  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-size: ${rem(22)};
  font-weight: 700;
  line-height: 1.36;
  color: ${MAROON};
  max-width: ${rem(520)};
  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(20)};
    line-height: 1.3;
  }
`;

const TitleAccent = styled.span`
  color: ${RED};
`;

const Desc = styled.p`
  margin: 0;
  font-size: ${rem(18)};
  line-height: 1.44;
  color: ${DARK};
  max-width: ${rem(400)};
  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(15)};
  }
`;

const Cta = styled(PillButton)`
  ${({ theme }) => theme.media('md')} {
    flex: 0 0 auto;
    align-self: center;
  }
`;

const GiftBanner = () => (
  <Card>
    <Img aria-hidden="true">
      <Collage
        src={`${ASSET}/promo-darcek-collage.png`}
        alt=""
        aria-hidden="true"
      />
      <Badge
        src={`${ASSET}/promo-darcek-badge.png`}
        alt=""
        aria-hidden="true"
      />
      <ImgTag>
        <img src={`${ASSET}/ico-gift.svg`} alt="" aria-hidden="true" />
        {__('Darček k nákupu')}
      </ImgTag>
    </Img>
    <Body>
      <Info>
        <GiftTag>
          <img src={`${ASSET}/ico-gift.svg`} alt="" aria-hidden="true" />
          {__('Darček k nákupu')}
        </GiftTag>
        <Title>
          <TitleAccent>{__('Darček k nákupu nad 40 €')}</TitleAccent>{' '}
          {__('- vzdelávacie materiály pre vaše dieťa')}
        </Title>
        <Desc>
          {__(
            'Pri nákupe nad 40 € získate darček - vzdelávacie materiály v hodnote 40 € - zadarmo',
          )}
        </Desc>
      </Info>
      <Cta to={'/darcek-k-nakupu40'}>{__('Zistiť viac o ponuke')}</Cta>
    </Body>
  </Card>
);

export default GiftBanner;
